import React from 'react';
import { MousePointer, BoxSelect, User, Car, MapPin, Phone, FileText, Trash2, Copy, Maximize2, StickyNote, GitBranch as GitBranch2 } from 'lucide-react';
import { NODE_TYPES } from '../types';

type BoardTheme = 'light' | 'dark';

interface ToolbarProps {
  selectedTool: string;
  snapToGrid: boolean;
  theme: BoardTheme;
  onToolChange: (tool: string) => void;
  onToggleSnap: () => void;
  onApplyLayout: () => void;
  onFitView?: () => void;
  onAddStickyNote?: () => void;
  hasSelection: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({
  selectedTool,
  snapToGrid,
  theme,
  onToolChange,
  onToggleSnap,
  onAddNode,
  onDeleteSelected,
  onDuplicateSelected,
  onApplyLayout,
  onFitView,
  onAddStickyNote,
  hasSelection
}) => {
  const nodeTypes = [
    { type: 'person' as const, icon: <User size={16} />, label: 'Person', tooltip: 'Add Person entity (1)' },
    { type: 'vehicle' as const, icon: <Car size={16} />, label: 'Vehicle', tooltip: 'Add Vehicle entity (2)' },
    { type: 'location' as const, icon: <MapPin size={16} />, label: 'Location', tooltip: 'Add Location entity (3)' },
    { type: 'phone' as const, icon: <Phone size={16} />, label: 'Phone', tooltip: 'Add Phone entity (4)' },
    { type: 'generic' as const, icon: <FileText size={16} />, label: 'Generic', tooltip: 'Add Generic entity (5)' }
  ];

  return (
    <div
      className="
        fixed z-20 p-1.5 flex shadow-2xl transition-all duration-300 rounded-xl border backdrop-blur-md
        bg-white/95 border-gray-200 dark:bg-slate-900/95 dark:border-slate-700
        lg:left-4 lg:top-1/2 lg:-translate-y-1/2 lg:flex-col lg:items-center lg:w-fit lg:space-y-1.5
        max-lg:bottom-6 max-lg:left-1/2 max-lg:-translate-x-1/2 max-lg:flex-row max-lg:items-center max-lg:w-auto max-lg:max-w-[95vw] max-lg:overflow-x-auto max-lg:no-scrollbar
      "
    >
      {/* Tools: Select, Box Select, Sticky Note */}
      <div className="flex lg:flex-col items-center gap-1.5">
        {/* Select Tool */}
        <button
          onClick={() => onToolChange('select')}
          className={`
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border transition-all duration-200 
            ${selectedTool === 'select'
              ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg scale-105'
              : 'bg-transparent border-transparent text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-white'
            }
          `}
          title="Select and move nodes (S)"
        >
          <MousePointer size={18} />
        </button>

        {/* Box Select (Marquee) Tool */}
        <button
          onClick={() => onToolChange('boxSelect')}
          className={`
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border transition-all duration-200 
            ${selectedTool === 'boxSelect'
              ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg scale-105'
              : 'bg-transparent border-transparent text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-white'
            }
          `}
          title="Box Select — drag to select multiple nodes (B)"
        >
          <BoxSelect size={18} />
        </button>

        {/* Sticky Note Tool */}
        <button
          onClick={onAddStickyNote}
          className="
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border border-transparent transition-all duration-200
            bg-transparent text-gray-500 hover:bg-yellow-50 hover:text-yellow-600 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-yellow-400
          "
          title="Add Sticky Note (N)"
        >
          <StickyNote size={18} />
        </button>
      </div>

      <div className="
        lg:border-t lg:pt-1.5 lg:mt-1.5 lg:space-y-1.5
        max-lg:border-l max-lg:pl-1.5 max-lg:ml-1.5 max-lg:flex max-lg:flex-row max-lg:gap-1.5
        border-gray-200 dark:border-slate-700/50
      ">
        {/* Entities */}
        {nodeTypes.map((nodeType) => (
          <button
            key={nodeType.type}
            onClick={() => onAddNode(nodeType.type)}
            className="
              w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border border-transparent transition-all duration-200
              bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800 dark:hover:text-white hover:border-gray-300 dark:hover:border-slate-600
            "
            title={nodeType.tooltip}
            style={{ borderLeftColor: NODE_TYPES[nodeType.type].color, borderLeftWidth: '3px' }}
          >
            {nodeType.icon}
          </button>
        ))}
      </div>

      <div className="
        lg:border-t lg:pt-1.5 lg:mt-1.5 lg:space-y-1.5
        max-lg:border-l max-lg:pl-1.5 max-lg:ml-1.5 max-lg:flex max-lg:flex-row max-lg:gap-1.5
        border-gray-200 dark:border-slate-700/50
      ">
        {/* Actions */}
        <button
          onClick={onApplyLayout}
          className="
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border border-transparent transition-all duration-200
            bg-transparent text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400
          "
          title="Auto Layout (L)"
        >
          <GitBranch2 size={18} />
        </button>

        <button
          onClick={onFitView}
          className="
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border border-transparent transition-all duration-200
            bg-transparent text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400
          "
          title="Fit View (0)"
        >
          <Maximize2 size={18} />
        </button>

        <button
          onClick={onDuplicateSelected}
          disabled={!hasSelection}
          className={`
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border border-transparent transition-all duration-200
            ${!hasSelection
              ? 'text-gray-300 dark:text-gray-700 cursor-not-allowed'
              : 'text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400 cursor-pointer'
            }
          `}
          title="Duplicate (Ctrl+D)"
        >
          <Copy size={18} />
        </button>

        <button
          onClick={onDeleteSelected}
          disabled={!hasSelection}
          className={`
            w-10 h-10 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg border border-transparent transition-all duration-200
            ${!hasSelection
              ? 'text-gray-300 dark:text-gray-700 cursor-not-allowed'
              : 'text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-red-400 cursor-pointer'
            }
          `}
          title="Delete (Delete)"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

export default Toolbar;
